// Credit to https://github.com/ekrzeptowski/react-repo-widget?tab=MIT-1-ov-file

import React, { useCallback } from "react";
import {
  licenseSVG,
  githubSVG,
  watchSVG,
  starSVG,
  forkSVG,
  updateSVG,
} from "../css/react-repo-widget-svglib";

import "../css/react-repo-widget.scss";

function solveCount(count: number) {
  let countString = "";

  if (count > 1000000) {
    countString = Math.round(count / 1000000) + "M";
  } else if (count > 1000) {
    countString = Math.round(count / 1000) + "K";
  } else {
    countString = count.toString();
  }

  return countString;
}

function getLanguageColor(language: string)
{
  switch (language) {
    case "Assembly": return "#6E4C13";
    case "C": return "#ffc0cb";
    case "C#": return "#178600";
    case "C++": return "#f34b7d";
    case "CSS": return "#563d7c";
    case "Haskell": return "#5e5086";
    case "HTML": return "#e34c26";
    case "Java": return "#b07219";
    case "JavaScript": return "#f1e05a";
    case "Objective-C": return "#438eff";
    case "Objective-C++": return "#6866fb";
    case "Python": return "#3572A5";
    case "PHP": return "#4F5D95";
    case "TeX": return "#3D6117";
    case "TypeScript": return "#2b7489"
    default: return "#555555"
  }
}

const _defaultClass = "repo-widget";

function RepoCard({
  repo,
  center = false,
  squareAvatar = false,
  descriptionLine = 2,
  showLanguage = true,
  showLicense = true,
  className = _defaultClass,
}: RepoCardProps) {
  const avatar_url = repo.owner.avatarUrl;
  const description = repo.description;
  const forks_count = repo.forks.totalCount;
  const language =
    repo.primaryLanguage === null ? "" : repo.primaryLanguage.name;
  const license =
    repo.licenseInfo === null ? undefined : repo.licenseInfo.spdxId;
  const repoName = repo.name;
  const stars_count = solveCount(repo.stargazerCount);
  const pushed_at = repo.pushedAt.slice(2, 10);
  const watchers_count = solveCount(repo.watchers.totalCount);
  const username = repo.owner.login;

  const renderCardHeader = useCallback(() => {
    let userLink = "https://github.com/" + username;
    let repoLink = "https://github.com/" + username + "/" + repoName;
    let renderLanguage =
      showLanguage && language !== undefined && language !== null;
    let languageSpan = renderLanguage ? (
      <span className={`${className}__headerStatus`} key="1">
        <span
          style={{
            backgroundColor: getLanguageColor(language),
          }}
        />
        <strong>{language}</strong>
      </span>
    ) : null;
    let renderLicense =
      showLicense && license !== undefined && license !== "NOASSERTION";
    let licenseSpan = renderLicense ? (
      <span className={`${className}__headerStatus`} key="2">
        {licenseSVG}
        <strong>{license}</strong>
      </span>
    ) : null;
    let secondLine =
      (renderLanguage && renderLicense && repoName.length > 15) ||
      ((renderLanguage || renderLicense) && repoName.length > 20) ? (
        <p
          className={`${className}__cardP`}
          style={{
            marginTop: "-7px",
            transform: "translateX(-3px)",
          }}
        >
          {languageSpan}
          {licenseSpan}
        </p>
      ) : undefined;
    let firstLineChildren = [
      <a
        className={`${className}__repo`}
        key="0"
        href={repoLink}
        target="_blank"
        rel="noreferrer"
      >
        <strong>{repoName}</strong>
      </a>,
    ];

    if (secondLine === undefined) {
      languageSpan && firstLineChildren.push(languageSpan);
      // licenseSpan && firstLineChildren.push(licenseSpan);
    }

    return (
      <div>
        <a
          className={`${className}__avatar`}
          style={{
            marginTop: secondLine ? "7px" : "",
          }}
          href={userLink}
          target="_blank"
          rel="noreferrer"
        >
          <img
            src={`${avatar_url}&s=144`}
            alt={`@${username}`}
            style={{
              borderRadius: squareAvatar ? "5px" : "50%",
            }}
          />
        </a>
        <a
          className={`${className}__buttonStar`}
          href={repoLink}
          target="_blank"
          rel="noreferrer"
        >
          Star {githubSVG}
        </a>
        <p className={`${className}__cardP`}>{firstLineChildren}</p>
        {secondLine}
        <p className={`${className}__cardP`}>
          Created by&nbsp;
          <a
            className={`${className}__creator`}
            href={userLink}
            target="_blank"
            rel="noreferrer"
          >
            {username}
          </a>
        </p>
      </div>
    );
  }, [
    username,
    repoName,
    avatar_url,
    license,
    language,
    showLanguage,
    showLicense,
    squareAvatar,
    className,
  ]);
  const renderCardContent = useCallback(() => {
    return (
      <div className={`${className}__content`}>
        <p
          className={`${className}__contentP`}
          style={{
            WebkitLineClamp: descriptionLine,
          }}
        >
          {description}
        </p>
      </div>
    );
  }, [description, descriptionLine, className]);
  const renderCardFooter = useCallback(() => {
    return (
      <div className={`${className}__footer`}>
        <div className={`footerStatus`}>
          {watchSVG} <span>Watch</span> <strong>{watchers_count}</strong>
        </div>
        <div className={`footerStatus`}>
          {starSVG} <span>Stars</span> <strong>{stars_count}</strong>
        </div>
        <div className={`footerStatus`}>
          {forkSVG} <span>Forks</span> <strong>{forks_count}</strong>
        </div>
        <div className={`${className}__spacer`} />
        <div className={`footerStatus update`}>
          {updateSVG} <span>{pushed_at}</span>
        </div>
      </div>
    );
  }, [watchers_count, stars_count, pushed_at, forks_count, className]);

  return (
    <div
      className={className}
      style={{
        margin: center ? "0 auto" : "",
      }}
    >
      {renderCardHeader()}
      {renderCardContent()}
      {renderCardFooter()}
    </div>
  );
}

export interface Repo {
  name: string;
  owner: {
    avatarUrl: string;
    login: string;
  };
  description: string;
  forks: { totalCount: number };
  primaryLanguage: { name: string } | null;
  licenseInfo: { spdxId: string } | null;
  pushedAt: string;
  stargazerCount: number;
  watchers: { totalCount: number };
}

export interface RepoCardProps {
  repo: Repo;
  center?: boolean;
  squareAvatar?: boolean;
  descriptionLine?: number;
  showLanguage?: boolean;
  showLicense?: boolean;
  className?: string;
}

export { RepoCard };
